"use client";

import { Loader, Mail, MapPin, Phone, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useState } from "react";
function BookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [serviceType, setServiceType] = useState<string>("DÉBOUCHAGE");
  const [consent, setConsent] = useState<boolean>(false);
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-primary bg-background rounded-md shadow-lg">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-3">
          [04] CONFIRMATION
        </p>
        <h3 className="font-default text-3xl font-semibold text-foreground mb-4">
          {successMessage}
        </h3>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-md">
          NOUVELLE DEMANDE
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <input type="hidden" name="service_type" value={serviceType} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="name" rules={{
        required: "Le nom est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                NOM COMPLET <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="VOTRE NOM" className="border-0 border-b border-border bg-transparent px-0 font-mono text-sm uppercase tracking-wider text-foreground placeholder:text-muted-foreground/40 placeholder:uppercase placeholder:text-xs focus-visible:ring-0 focus-visible:border-primary h-12 rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "L'email est requis",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Email invalide"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                ADRESSE COURRIEL <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="NOM@EXEMPLE.COM" className="border-0 border-b border-border bg-transparent px-0 font-mono text-sm uppercase tracking-wider text-foreground placeholder:text-muted-foreground/40 placeholder:uppercase placeholder:text-xs focus-visible:ring-0 focus-visible:border-primary h-12 rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Le téléphone est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                TÉLÉPHONE <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="438-367-8309" className="border-0 border-b border-border bg-transparent px-0 font-mono text-sm uppercase tracking-wider text-foreground placeholder:text-muted-foreground/40 placeholder:uppercase placeholder:text-xs focus-visible:ring-0 focus-visible:border-primary h-12 rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="location" rules={{
        required: "La localisation est requise"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                VILLE / SECTEUR <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="border-0 border-b border-border bg-transparent px-0 font-mono text-sm uppercase tracking-wider text-foreground h-12 focus-visible:ring-0 focus-visible:border-primary w-full data-[placeholder]:text-muted-foreground/40 rounded-md">
                    <SelectValue placeholder="SÉLECTIONNEZ VOTRE VILLE" />
                  </SelectTrigger>
                  <SelectContent className="border-border bg-popover">
                    <SelectItem value="Montreal" className="font-mono uppercase text-xs">Montréal</SelectItem>
                    <SelectItem value="Laval" className="font-mono uppercase text-xs">Laval</SelectItem>
                    <SelectItem value="Longueuil" className="font-mono uppercase text-xs">Longueuil</SelectItem>
                    <SelectItem value="Rive-Nord" className="font-mono uppercase text-xs">Rive-Nord</SelectItem>
                    <SelectItem value="Rive-Sud" className="font-mono uppercase text-xs">Rive-Sud</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" rules={{
        required: "La date est requise"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                DATE SOUHAITÉE <span className="text-primary">*</span>
              </FormLabel>
              <FormControl>
                <Input type="date" className="border-0 border-b border-border bg-transparent px-0 font-mono text-sm uppercase tracking-wider text-foreground placeholder:text-muted-foreground/40 focus-visible:ring-0 focus-visible:border-primary h-12 rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_time" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                PLAGE HORAIRE
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="border-0 border-b border-border bg-transparent px-0 font-mono text-sm uppercase tracking-wider text-foreground h-12 focus-visible:ring-0 focus-visible:border-primary w-full data-[placeholder]:text-muted-foreground/40 rounded-md">
                    <SelectValue placeholder="SÉLECTIONNEZ UN CRÉNEAU" />
                  </SelectTrigger>
                  <SelectContent className="border-border bg-popover">
                    <SelectItem value="Matin (8h - 12h)" className="font-mono uppercase text-xs">Matin (8h - 12h)</SelectItem>
                    <SelectItem value="Après-midi (12h - 16h)" className="font-mono uppercase text-xs">Après-midi (12h - 16h)</SelectItem>
                    <SelectItem value="Soir (16h - 20h)" className="font-mono uppercase text-xs">Soir (16h - 20h)</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div>
        <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3 block">
          TYPE D'INTERVENTION
        </FormLabel>
        <ToggleGroup type="single" value={serviceType} onValueChange={v => v && setServiceType(v)} className="w-full grid grid-cols-2 md:grid-cols-4 gap-0">
          {["DÉBOUCHAGE", "INSPECTION", "URGENCE", "ENTRETIEN"].map((opt, i) => <ToggleGroupItem key={opt} data-index={i} value={opt} className="border border-border font-mono text-xs uppercase tracking-widest h-12 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground hover:bg-primary/10 rounded-md">
              {opt}
            </ToggleGroupItem>)}
        </ToggleGroup>
      </div>

      <FormField control={control} name="notes" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              DÉTAILS SUPPLÉMENTAIRES
            </FormLabel>
            <FormControl>
              <Textarea placeholder="DÉCRIVEZ LE PROBLÈME (EX: DRAIN BLOQUÉ, RECOURS À LA CAMÉRA...)" className="border-0 border-b border-border bg-transparent px-0 font-mono text-sm tracking-wider text-foreground placeholder:text-muted-foreground/40 placeholder:uppercase placeholder:text-xs focus-visible:ring-0 focus-visible:border-primary min-h-24 resize-none rounded-md" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="flex items-center gap-3 pt-2">
        <Checkbox id="consent" checked={consent} onCheckedChange={v => setConsent(Boolean(v))} className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground rounded-md" />
        <label htmlFor="consent" className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground cursor-pointer">
          J'ACCEPTE LES CONDITIONS DE SERVICE <span className="text-primary">*</span>
        </label>
      </div>

      {submitError && <div className="p-4 border border-destructive bg-destructive/10 rounded-md">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting || !consent} className="w-full h-16 bg-primary text-primary-foreground hover:bg-primary/90 font-default font-semibold text-base uppercase tracking-[0.15em] shadow-md disabled:opacity-50 rounded-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            ENVOI EN COURS...
          </> : <>CONFIRMER LA DEMANDE →</>}
      </Button>
    </Form>;
}
export default function DemanderUneIntervention() {
  return <section data-section-id="2583" id="demander-une-intervention" className="dark bg-background text-foreground w-full">
      <div className="grid grid-cols-1 lg:grid-cols-[45%_55%] min-h-[180px]">
        {}
        <div className="bg-background text-foreground px-6 sm:px-10 lg:px-16 py-16 lg:py-24 flex flex-col justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-8">
              [03] PLANIFICATION
            </p>
            <h2 className="font-default font-semibold text-5xl sm:text-6xl lg:text-7xl xl:text-[72px] leading-[0.95] text-foreground mb-8 tracking-tighter">
              DEMANDER UNE{" "}
              <span className="text-primary">INTERVENTION.</span>
            </h2>
            <p className="font-default text-base lg:text-lg text-muted-foreground leading-relaxed max-w-md mb-12">
              Nos techniciens spécialisés en débouchage et inspection par caméra interviennent rapidement. Diagnostic précis, prix transparents à partir de 275$ et service garanti.
            </p>

            <div className="space-y-0 border-t border-border/20">
              <div className="flex items-center gap-3 py-5 border-b border-border/20">
                <MapPin className="size-4 text-primary shrink-0" />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/80">
                  ZONE / MONTRÉAL, LAVAL, LONGUEUIL / 24/7
                </p>
              </div>
              <div className="flex items-center gap-3 py-5 border-b border-border/20">
                <Phone className="size-4 text-primary shrink-0" />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/80">
                  TÉLÉPHONE / 438-367-8309
                </p>
              </div>
              <div className="flex items-center gap-3 py-5 border-b border-border/20">
                <Mail className="size-4 text-primary shrink-0" />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/80">
                  EMAIL / CONTACT@OTSOLUTION.CA
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 backdrop-blur-md bg-primary/5 border border-primary/20 p-5 shadow-xl max-w-sm rounded-md">
            <div className="flex items-center gap-3">
              <div className="relative flex items-center justify-center">
                <span className="absolute size-3 rounded-md bg-secondary animate-ping opacity-75"></span>
                <span className="relative size-2 rounded-md bg-secondary"></span>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">
                  PROCHAINE DISPONIBILITÉ
                </p>
                <p className="font-mono text-sm uppercase tracking-wider text-foreground font-semibold">
                  AUJOURD'HUI 14:00
                </p>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="bg-card text-card-foreground px-6 sm:px-10 lg:px-16 xl:px-20 py-16 lg:py-20">
          <FormProvider formKey="intervention_form_unclogging" formId="intervention_form_unclogging" sectionName="demander-une-intervention" defaultValues={{
          name: "",
          email: "",
          phone: "",
          location: "",
          preferred_date: "",
          preferred_time: "",
          notes: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true
          },
          location: {
            required: true
          },
          preferred_date: {
            required: true
          }
        }} submitText="CONFIRMER LA DEMANDE" successMessage="Demande reçue. Nous vous contacterons dans les plus brefs délais pour confirmer." errorMessage="L'envoi a échoué. Veuillez réessayer ou nous appeler directement." className="text-foreground">
            <div className="flex items-center gap-3 mb-10">
              <Zap className="size-6 text-primary" fill="currentColor" />
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
                REQUÊTE DE SERVICE
              </p>
            </div>
            <h3 className="font-default font-semibold text-3xl lg:text-[32px] leading-tight text-foreground mb-10 tracking-tight">
              PLANIFIEZ VOTRE RENDEZ-VOUS
            </h3>

            <div className="[&_label]:!text-muted-foreground [&_input]:!text-foreground [&_input]:!border-border [&_input::placeholder]:!text-muted-foreground/40 [&_textarea]:!text-foreground [&_textarea]:!border-border [&_textarea::placeholder]:!text-muted-foreground/40 [&_[data-slot=select-trigger]]:!text-foreground [&_[data-slot=select-trigger]]:!border-border [&_[data-slot=select-trigger][data-placeholder]]:!text-muted-foreground/40 [&_[data-slot=toggle-group-item]]:!border-border [&_[data-slot=toggle-group-item]]:!text-foreground [&_[data-slot=checkbox]]:!border-border [&_label[for=consent]]:!text-foreground">
              <BookingForm />
            </div>
          </FormProvider>
        </div>
      </div>
    </section>;
}